import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const characters = {
  valentino: {
    name: 'Valentino',
    chapter: 'Chapter 8 - Attend Polo',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face',
    video: '/videos/valentino/Man_Enters_Porsche_Golden_Hour.mp4',
    video2: '/videos/valentino/video2.mp4',
    likes: 557,
  },
  sophia: {
    name: 'Sophia',
    chapter: 'Chapter 3 - Gallery Opening',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&crop=face',
    video: 'https://assets.mixkit.co/videos/preview/mixkit-woman-walking-at-the-beach-1209-large.mp4',
    likes: 423,
  },
  marcus: {
    name: 'Marcus',
    chapter: 'Chapter 5 - Startup Pitch',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face',
    video: 'https://assets.mixkit.co/videos/preview/mixkit-man-working-on-a-laptop-at-home-4814-large.mp4',
    likes: 892,
  },
  elena: {
    name: 'Elena',
    chapter: 'Chapter 12 - Paris Adventure',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face',
    video: 'https://assets.mixkit.co/videos/preview/mixkit-girl-in-neon-sign-1232-large.mp4',
    likes: 1205,
  },
};

const options = [
  { id: 1, text: 'Get Weird', likes: 10 },
  { id: 2, text: 'Option 2', likes: 15 },
  { id: 3, text: 'Option 3', likes: 20 },
  { id: 4, text: 'Option 4', likes: 30 },
  { id: 5, text: 'Option 5', likes: 25 },
  { id: 6, text: 'Option 6', likes: 18 },
];

function CharacterPage() {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [currentVideo, setCurrentVideo] = useState(null);
  const [isPlayingSpecial, setIsPlayingSpecial] = useState(false);
  const videoRef = useRef(null);

  const character = characters[characterId];

  useEffect(() => {
    if (character) {
      setCurrentVideo(character.video);
    }
  }, [character]);

  if (!character) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <p>Character not found</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-pink-400 underline"
          >
            Go back home
          </button>
        </div>
      </div>
    );
  }

  const handleSend = () => {
    if (message.trim()) {
      console.log('Sending message:', message);
      setMessage('');
    }
  };

  const handleOptionClick = (option) => {
    // Switch to video2 when "Get Weird" is clicked
    if (option.id === 1 && character.video2) {
      setIsPlayingSpecial(true);
      setCurrentVideo(character.video2);
    }
  };

  const handleVideoEnded = () => {
    if (isPlayingSpecial) {
      setIsPlayingSpecial(false);
      setCurrentVideo(character.video);
    }
  };

  return (
    <div className="h-screen w-full relative overflow-hidden bg-black">
      {/* Background Video */}
      {currentVideo && (
        <video
          key={currentVideo}
          ref={videoRef}
          autoPlay
          loop={!isPlayingSpecial}
          muted
          playsInline
          onEnded={handleVideoEnded}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={currentVideo} type="video/mp4" />
        </video>
      )}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80" />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <img
              src={character.avatar}
              alt={character.name}
              className="w-10 h-10 rounded-full object-cover border-2 border-white/50"
            />
            <div>
              <h1 className="text-white font-semibold">{character.name}</h1>
              <p className="text-white/70 text-xs">{character.chapter}</p>
            </div>
          </div>

          {/* Likes Counter */}
          <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1">
            <svg className="w-5 h-5 text-pink-500 fill-pink-500" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            <span className="text-white font-medium">{character.likes}</span>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom Section */}
        <div className="p-4 space-y-3">
          {/* Scrollable Options */}
          <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleOptionClick(option)}
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full border transition-all bg-black/40 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 active:bg-pink-500 active:border-pink-500"
              >
                <span>{option.text}</span>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4 text-pink-400 fill-pink-400" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                  <span className="text-sm">{option.likes}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Text Input */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Say something..."
              className="flex-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-pink-400"
            />
            <button
              onClick={handleSend}
              className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg"
            >
              <svg className="w-5 h-5 text-white fill-white" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CharacterPage;
